import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL } from '@/services/api'
import {
  CreditCardIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  CalendarIcon,
  BanknotesIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'

interface Plan {
  id: number
  name: string
  slug: string
  description: string
  price: number
  currency: string
  interval: string
  trial_days: number
  features: any
  is_popular: boolean
}

interface Subscription {
  id: number
  plan: Plan
  status: string
  current_period_start: string
  current_period_end: string
  trial_start: string | null
  trial_end: string | null
  cancel_at_period_end: boolean
  is_trialing: boolean
}

interface Invoice {
  id: number
  invoice_number: string
  amount: number
  currency: string
  status: string
  issue_date: string
  due_date: string
  paid_at: string | null
  payment_url: string | null
}

interface BillingInfo {
  tenant_name: string
  tenant_status: string
  subscription: Subscription
  available_plans: Plan[]
  recent_invoices: Invoice[]
}

export default function BillingPage() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  useEffect(() => {
    loadBillingInfo()
  }, [])

  const loadBillingInfo = async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      navigate('/login')
      return
    }

    try {
      const response = await fetch(`${API_BASE_URL}/billing/info`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setBillingInfo(data)
      } else {
        console.error('Error loading billing info')
      }
    } catch (err) {
      console.error('Error:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleChangePlan = async () => {
    if (!selectedPlan) return

    setActionLoading(true)
    const token = localStorage.getItem('token')

    try {
      const response = await fetch(`${API_BASE_URL}/billing/subscription`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_plan_slug: selectedPlan.slug })
      })

      if (response.ok) {
        await loadBillingInfo()
        setShowUpgradeModal(false)
        setSelectedPlan(null)
      } else {
        const error = await response.json()
        alert(`Erro ao alterar plano: ${error.detail}`)
      }
    } catch (err) {
      console.error('Error changing plan:', err)
      alert('Erro ao alterar plano')
    } finally {
      setActionLoading(false)
    }
  }

  const handleCancelSubscription = async (immediately: boolean) => {
    setActionLoading(true)
    const token = localStorage.getItem('token')

    try {
      const response = await fetch(`${API_BASE_URL}/billing/subscription/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cancel_immediately: immediately })
      })

      if (response.ok) {
        await loadBillingInfo()
        setShowCancelModal(false)
        if (immediately) {
          navigate('/login')
        }
      } else {
        const error = await response.json()
        alert(`Erro ao cancelar: ${error.detail}`)
      }
    } catch (err) {
      console.error('Error cancelling:', err)
      alert('Erro ao cancelar assinatura')
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; text: string; icon: any }> = {
      'active': { color: 'green', text: 'Ativo', icon: CheckCircleIcon },
      'trialing': { color: 'blue', text: 'Período de Teste', icon: CalendarIcon },
      'cancelled': { color: 'red', text: 'Cancelado', icon: XCircleIcon },
      'suspended': { color: 'yellow', text: 'Suspenso', icon: ExclamationTriangleIcon }
    }

    const badge = badges[status] || badges['active']
    const Icon = badge.icon

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-${badge.color}-100 text-${badge.color}-700`}>
        <Icon className="h-4 w-4 mr-1" />
        {badge.text}
      </span>
    )
  }

  const getInvoiceStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'paid': 'green',
      'pending': 'yellow',
      'overdue': 'red',
      'cancelled': 'gray'
    }

    const labels: Record<string, string> = {
      'paid': 'Pago',
      'pending': 'Pendente',
      'overdue': 'Vencido',
      'cancelled': 'Cancelado'
    }

    const color = colors[status] || 'gray'
    const label = labels[status] || status

    return (
      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium bg-${color}-100 text-${color}-700`}>
        {label}
      </span>
    )
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR')
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount)
  }

  const getDaysRemaining = (endDate: string) => {
    const days = Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    return days > 0 ? days : 0
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!billingInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Erro ao carregar informações de cobrança</p>
      </div>
    )
  }

  const { subscription, available_plans, recent_invoices } = billingInfo

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Assinatura e Cobrança</h1>
          <p className="text-gray-600">Gerencie seu plano, pagamentos e faturas</p>
        </div>

        {/* Current Subscription Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Plano Atual</h2>
              {getStatusBadge(subscription.is_trialing ? 'trialing' : subscription.status)}
            </div>
            <CreditCardIcon className="h-8 w-8 text-gray-400" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Plan Details */}
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">
                {subscription.plan.name}
              </h3>
              <p className="text-gray-600 mb-4">{subscription.plan.description}</p>

              <div className="flex items-baseline mb-4">
                <span className="text-3xl font-bold text-gray-900">
                  {formatCurrency(subscription.plan.price)}
                </span>
                <span className="text-gray-600 ml-2">/mês</span>
              </div>

              {subscription.is_trialing && subscription.trial_end && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <CalendarIcon className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Período de Teste</p>
                      <p className="text-sm text-gray-600">
                        Restam <span className="font-bold text-blue-600">
                          {getDaysRemaining(subscription.trial_end)} dias
                        </span> de teste grátis
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {subscription.cancel_at_period_end && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">Cancelamento Agendado</p>
                      <p className="text-sm text-gray-600">
                        Sua assinatura será cancelada em {formatDate(subscription.current_period_end)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Billing Period */}
            <div>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Período de Cobrança</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Início:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(subscription.current_period_start)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Próxima cobrança:</span>
                    <span className="font-medium text-gray-900">
                      {formatDate(subscription.current_period_end)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                >
                  Alterar Plano
                </button>

                {!subscription.cancel_at_period_end && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="w-full px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                  >
                    Cancelar Assinatura
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Method Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Forma de Pagamento</h2>
            <ShieldCheckIcon className="h-6 w-6 text-gray-400" />
          </div>

          <div className="bg-gray-50 rounded-lg p-6 text-center">
            <BanknotesIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 mb-2">Nenhuma forma de pagamento configurada</p>
            <p className="text-sm text-gray-500 mb-4">
              O gateway de pagamento será configurado em breve
            </p>
            <button
              disabled
              className="px-6 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed font-medium"
            >
              Adicionar Cartão (Em Breve)
            </button>
          </div>
        </div>

        {/* Invoice History */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Histórico de Faturas</h2>
            <DocumentTextIcon className="h-6 w-6 text-gray-400" />
          </div>

          {recent_invoices.length === 0 ? (
            <div className="text-center py-8">
              <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600">Nenhuma fatura encontrada</p>
              <p className="text-sm text-gray-500">
                Suas faturas aparecerão aqui quando forem geradas
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Número</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Data</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Vencimento</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Valor</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_invoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">
                        {invoice.invoice_number}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDate(invoice.issue_date)}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {formatDate(invoice.due_date)}
                      </td>
                      <td className="py-3 px-4 text-sm font-medium text-gray-900">
                        {formatCurrency(invoice.amount)}
                      </td>
                      <td className="py-3 px-4">
                        {getInvoiceStatusBadge(invoice.status)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {invoice.payment_url ? (
                          <a
                            href={invoice.payment_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            Pagar
                          </a>
                        ) : invoice.status === 'paid' ? (
                          <button className="text-gray-600 hover:text-gray-700 text-sm font-medium">
                            Download
                          </button>
                        ) : (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Upgrade/Change Plan Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">Alterar Plano</h3>
              <p className="text-gray-600 mt-1">Escolha o plano ideal para suas necessidades</p>
            </div>

            <div className="p-6">
              <div className="grid md:grid-cols-3 gap-4">
                {available_plans.map((plan) => {
                  const isCurrent = plan.id === subscription.plan.id
                  const isUpgrade = plan.price > subscription.plan.price
                  const isDowngrade = plan.price < subscription.plan.price

                  return (
                    <div
                      key={plan.id}
                      className={`border rounded-lg p-4 cursor-pointer transition ${
                        isCurrent
                          ? 'border-gray-400 bg-gray-50'
                          : selectedPlan?.id === plan.id
                          ? 'border-blue-600 bg-blue-50'
                          : 'border-gray-200 hover:border-blue-400'
                      }`}
                      onClick={() => !isCurrent && setSelectedPlan(plan)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-bold text-gray-900">{plan.name}</h4>
                        {isCurrent && (
                          <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-full">
                            Atual
                          </span>
                        )}
                        {!isCurrent && isUpgrade && (
                          <ArrowUpIcon className="h-4 w-4 text-green-600" />
                        )}
                        {!isCurrent && isDowngrade && (
                          <ArrowDownIcon className="h-4 w-4 text-orange-600" />
                        )}
                      </div>

                      <div className="mb-2">
                        <span className="text-2xl font-bold text-gray-900">
                          {formatCurrency(plan.price)}
                        </span>
                        <span className="text-gray-600 text-sm">/mês</span>
                      </div>

                      <p className="text-xs text-gray-600 mb-3">{plan.description}</p>

                      {plan.features && (
                        <div className="space-y-1">
                          {plan.features.users && (
                            <div className="flex items-center text-xs text-gray-600">
                              <CheckCircleIcon className="h-3 w-3 text-green-500 mr-1" />
                              <span>
                                {plan.features.users === null ? 'Usuários ilimitados' : `${plan.features.users} usuários`}
                              </span>
                            </div>
                          )}
                          {plan.features.transactions && (
                            <div className="flex items-center text-xs text-gray-600">
                              <CheckCircleIcon className="h-3 w-3 text-green-500 mr-1" />
                              <span>
                                {plan.features.transactions === null ? 'Transações ilimitadas' : `${plan.features.transactions} transações/mês`}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowUpgradeModal(false)
                  setSelectedPlan(null)
                }}
                disabled={actionLoading}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleChangePlan}
                disabled={!selectedPlan || actionLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition font-medium"
              >
                {actionLoading ? 'Processando...' : 'Confirmar Alteração'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mx-auto mb-4">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                Cancelar Assinatura?
              </h3>
              <p className="text-gray-600 text-center mb-6">
                Você tem certeza que deseja cancelar sua assinatura? Esta ação não pode ser desfeita.
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700 mb-3">
                  <strong>Escolha uma opção:</strong>
                </p>
                <div className="space-y-2">
                  <button
                    onClick={() => handleCancelSubscription(false)}
                    disabled={actionLoading}
                    className="w-full text-left px-4 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  >
                    <p className="font-medium text-gray-900 text-sm">Cancelar no final do período</p>
                    <p className="text-xs text-gray-600">
                      Acesso até {formatDate(subscription.current_period_end)}
                    </p>
                  </button>
                  <button
                    onClick={() => handleCancelSubscription(true)}
                    disabled={actionLoading}
                    className="w-full text-left px-4 py-3 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition"
                  >
                    <p className="font-medium text-red-600 text-sm">Cancelar imediatamente</p>
                    <p className="text-xs text-gray-600">
                      Perda de acesso instantânea
                    </p>
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={actionLoading}
                className="w-full px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
